import * as React from 'react';
import { produce } from 'immer';
import { createContainer } from 'react-tracked';
import { isAIEnriching, isProcessing } from '@studio/pages/Library/helpers';
import type { LibrarySearch } from '@studio/types';

type BulkDeleteContextState = {
  isSelecting: boolean;
  selectedVideos: LibrarySearch.Get.MediaItem[];
};

const initialBulkDeleteState: BulkDeleteContextState = {
  isSelecting: false,
  selectedVideos: [],
};

enum BULK_DELETE_ACTION_TYPES {
  DESELECT_ALL,
  RESET,
  SELECT_ALL,
  SELECTED_VIDEOS,
  SET_IS_SELECTING,
}

type BulkDeleteActions =
  | {
      payload: boolean;
      type: BULK_DELETE_ACTION_TYPES.SET_IS_SELECTING;
    }
  | {
      payload: LibrarySearch.Get.MediaItem;
      type: BULK_DELETE_ACTION_TYPES.SELECTED_VIDEOS;
    }
  | {
      payload: LibrarySearch.Get.MediaItem[];
      type: BULK_DELETE_ACTION_TYPES.DESELECT_ALL;
    }
  | {
      payload: LibrarySearch.Get.MediaItem[];
      type: BULK_DELETE_ACTION_TYPES.SELECT_ALL;
    }
  | {
      type: BULK_DELETE_ACTION_TYPES.RESET;
    };

const bulkDeleteReducer = (state: BulkDeleteContextState, action: BulkDeleteActions) => {
  switch (action.type) {
    case BULK_DELETE_ACTION_TYPES.SET_IS_SELECTING:
      state.isSelecting = action.payload;
      break;
    case BULK_DELETE_ACTION_TYPES.SELECTED_VIDEOS:
      if (state.selectedVideos.some((item) => item.mediaId === action.payload.mediaId)) {
        state.selectedVideos = state.selectedVideos.filter((item) => item.mediaId !== action.payload.mediaId);
      } else {
        state.selectedVideos = [...state.selectedVideos, action.payload];
      }
      break;
    case BULK_DELETE_ACTION_TYPES.SELECT_ALL: {
      state.selectedVideos = [
        ...state.selectedVideos,
        ...action.payload
          .filter((item) => !isProcessing(item) && !isAIEnriching(item))
          .filter((item) => !state.selectedVideos.some((selected) => selected.mediaId === item.mediaId))
          .slice(0, 100 - state.selectedVideos.length),
      ];
      break;
    }
    case BULK_DELETE_ACTION_TYPES.DESELECT_ALL:
      state.selectedVideos = state.selectedVideos.filter(
        (item) => !action.payload.some((pageItem) => pageItem.mediaId === item.mediaId)
      );
      break;
    case BULK_DELETE_ACTION_TYPES.RESET:
      state.isSelecting = false;
      state.selectedVideos = [];
      break;
  }
};

type BulkDeleteContextHook = BulkDeleteContextState & {
  deselectAll: (pageItems: LibrarySearch.Get.MediaItem[]) => void;
  reset: () => void;
  selectAll: (pageItems: LibrarySearch.Get.MediaItem[]) => void;
  setIsSelecting: (isSelecting: boolean) => void;
  setSelectedVideos: (item: LibrarySearch.Get.MediaItem) => void;
};

const curriedReducerFunction = produce(bulkDeleteReducer);

const useValues = (): readonly [BulkDeleteContextHook, () => void] => {
  const [localState, dispatch] = React.useReducer(curriedReducerFunction, {
    ...initialBulkDeleteState,
  });

  const setIsSelecting = React.useCallback((isSelecting: boolean) => {
    dispatch({
      type: BULK_DELETE_ACTION_TYPES.SET_IS_SELECTING,
      payload: isSelecting,
    });
  }, []);

  const setSelectedVideos = React.useCallback((item: LibrarySearch.Get.MediaItem) => {
    dispatch({
      type: BULK_DELETE_ACTION_TYPES.SELECTED_VIDEOS,
      payload: item,
    });
  }, []);

  const selectAll = React.useCallback((pageItems: LibrarySearch.Get.MediaItem[]) => {
    dispatch({
      type: BULK_DELETE_ACTION_TYPES.SELECT_ALL,
      payload: pageItems,
    });
  }, []);

  const deselectAll = React.useCallback((pageItems: LibrarySearch.Get.MediaItem[]) => {
    dispatch({
      type: BULK_DELETE_ACTION_TYPES.DESELECT_ALL,
      payload: pageItems,
    });
  }, []);

  const reset = React.useCallback(() => {
    dispatch({
      type: BULK_DELETE_ACTION_TYPES.RESET,
    });
  }, []);

  const state = React.useMemo(
    () => ({
      ...localState,
      deselectAll,
      reset,
      selectAll,
      setIsSelecting,
      setSelectedVideos,
    }),
    [deselectAll, localState, reset, selectAll, setIsSelecting, setSelectedVideos]
  );

  return [
    state,
    () => {
      throw new Error('use functions in the state');
    },
  ];
};

const {
  Provider: BulkDeleteProvider,
  useTrackedState: useBulkDelete,
  useUpdate: useBulkDeleteUpdate,
} = createContainer<BulkDeleteContextHook, () => void, { children: React.ReactNode }>(useValues);

export { BulkDeleteProvider, useBulkDelete, useBulkDeleteUpdate };
export type { BulkDeleteContextHook };
